import { Suspense, lazy, useEffect, useState, useMemo } from 'react';
import { StyledEngineProvider, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import enLocale from 'date-fns/locale/en-GB';
import { SnackbarProvider } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { fetchProfile } from './auth/authActions';
import { AuthService } from './auth/AuthService';
import { REFRESH_TOKEN_SUCCESS, RESET_TOKEN_SUCCESS } from './auth/authTypes';
import { addTokenToRequest } from './auth/interceptor';
import { Notifier } from './components/notification/Notifier';
import TopBar from './components/top-bar/TopBar';
import muiTheme from './muiTheme';
import { getStatusOptions } from '../store/constant-state/action';
import './scss/global.scss';
import Parent from './Parent';
import jwt_decode from 'jwt-decode';
import store from '../store';
import {
  COUNTRIES_FILTER_CALLS,
  COUNTRY_FILTER_CALLS,
  CUSTOM_COUNTRY_FILTER_CALLS,
} from './constants/api-constants';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license';
import { decideHomePage } from '../access-control/helpers';
import PageControl from './components/access-control/PageControl';
import { getUniqueUserCountries } from '../helpers/user.helpers';
import { fetchProfileFeatures, fetchFeaturesForCountries, fetchFeaturesForTeams } from '../new-store/features/actions';

import AccessDenied from '../pages/accessDenied';
import { featureSelector } from '../new-store/features/selectors';
import ImrReminder, { ImrReminderType } from './components/notification/ImrReminder';
import { isImpersonationReadOnlyError, notifyImpersonationReadOnly } from './components/impersonation/impersonation-read-only';
import { fetchImpersonation } from '../new-store/impersonation/actions';

// Trigger build, can be removed later

const NODE_ENV = import.meta.env.MODE;
const REACT_APP_MUI_X_PRO_LICENSE_KEY = import.meta.env.REACT_APP_MUI_X_PRO_LICENSE_KEY;

const LightView = lazy(() => import('../pages/light-view'));
const ContentProduction = lazy(() => import('../pages/content-production'));
const CampaignOverview = lazy(() => import('../pages/campaigns'));
const CampaignList = lazy(() => import('../pages/campaigns/list'));
const CreateCampaign = lazy(() => import('./pages/campaign-planning/create-campaign/CreateCampaign.tsx'));
const CustomerJourney = lazy(() => import('./pages/customer-journey/CustomerJourney'));
const Profile = lazy(() => import('./pages/campaign-profile/profile'));
const SwitchForCustomerJourney = lazy(() => import('./pages/switch-editor/SwitchForCustomerJourney'));
const SwitchForEvent = lazy(() => import('./pages/switch-editor/SwitchForEvent'));
const SwitchForUserTemplate = lazy(() => import('./pages/switch-editor/SwitchForUserTemplate'));
const SwitchLayout = lazy(() => import('./pages/switch-editor/Layout'));
const SwitchLayoutV2 = lazy(() => import('./pages/switch-editor-v2/Layout'));
const SwitchEmailV2 = lazy(() => import('../app/components/switch-editor-v2/app/SwitchEmailEditor'));
const SwitchTemplateV2 = lazy(() => import('../app/components/switch-editor-v2/app/SwitchTemplateStudio'));
const SwitchComponentV2 = lazy(() => import('../app/components/switch-editor-v2/app/SwitchComponentStudio'));
const SwitchColorSetV2 = lazy(() => import('../app/components/switch-editor-v2/app/SwitchColorSetStudio'));
const SwitchBrandV2 = lazy(() => import('../app/components/switch-editor-v2/app/SwitchBrandStudio'));
const SwitchBrandCreateV2 = lazy(() => import('../app/components/switch-editor-v2/app/SwitchBrandCreate'));
const SwitchColorSetCreateV2 = lazy(() => import('../app/components/switch-editor-v2/app/SwitchColorSetCreate'));
const PageNotFound = lazy(() => import('./helper/error'));
const CampaignsTimeline = lazy(() => import('../pages/campaigns-timeline'));
const AnnualCampaignMapsList = lazy(() => import('../pages/annual-campaign-maps/List'));
const AnnualCampaignMap = lazy(() => import('../pages/annual-campaign-maps/map'));
const UserProfile = lazy(() => import('./pages/user-profile/UserProfile'));

LicenseInfo.setLicenseKey(REACT_APP_MUI_X_PRO_LICENSE_KEY);

axios.interceptors.request.use(
  req => {
    let params = {};
    if (
      new RegExp(
        `${[
          ...COUNTRY_FILTER_CALLS,
          ...COUNTRIES_FILTER_CALLS,
          ...CUSTOM_COUNTRY_FILTER_CALLS,
        ].join('|')}`,
      ).test(req.url)
    ) {
      const campaignCountryIds = store
        .getState()
        .campaignProfile?.campaignProfile?.campaignDetails?.countries.map(country => country.id)
        .join(',');
      if (campaignCountryIds?.length) {
        switch (true) {
          case COUNTRY_FILTER_CALLS.length &&
            new RegExp(`${COUNTRY_FILTER_CALLS.join('|')}`).test(req.url):
            params = { filter: `country.id||$in||${campaignCountryIds}` };
            break;
          case COUNTRIES_FILTER_CALLS.length &&
            new RegExp(`${COUNTRIES_FILTER_CALLS.join('|')}`).test(req.url):
            params = { filter: `countries.id||$in||${campaignCountryIds}` };
            break;
          case CUSTOM_COUNTRY_FILTER_CALLS.length &&
            new RegExp(`${CUSTOM_COUNTRY_FILTER_CALLS.join('|')}`).test(req.url):
            params = { countryIds: campaignCountryIds };
            break;
          default:
        }
      }
    }

    req.params = {
      ...req.params,
      ...params,
    };

    return req;
  },
  err => Promise.reject(err),
);

// Surface the dedicated message when a mutation is blocked by a read-only
// impersonation session (this instance's auth-refresh only retries 401s, so the
// 403 reaches here untouched).
axios.interceptors.response.use(
  response => response,
  error => {
    if (isImpersonationReadOnlyError(error)) {
      notifyImpersonationReadOnly();
    }
    return Promise.reject(error);
  },
);

const MainLayout = () => (
  <>
    <TopBar />
    <div className="mainViewContainer">
      <Outlet />
    </div>
  </>
);

const AsyncPageNotFound = () => {
  return (
    <Suspense fallback={null}>
      <PageNotFound />
    </Suspense>
  )
}

const AsyncCreateCampaign = () => {
  return (
    <Suspense fallback={null}>
      <CreateCampaign />
    </Suspense>
  )
}

const AsyncLightView = () => {
  return (
    <Suspense fallback={null}>
      <PageControl code='light_view' fallback='/'>
        <LightView />
      </PageControl>
    </Suspense>
  )
}

const AsyncContentProduction = () => {
  return (
    <Suspense fallback={null}>
      <PageControl code='content_production'>
        <ContentProduction />
      </PageControl>
    </Suspense>
  )
}

const AsyncCampaignOverview = () => {
  return (
    <Suspense fallback={null}>
      <PageControl code='default_view' fallback='/'>
        <CampaignOverview />
      </PageControl>
    </Suspense>
  )
}

const AsyncCampaignList = ({ myList }) => {
  return (
    <Suspense fallback={null}>
      <CampaignList myList={myList} />
    </Suspense>
  )
}

const AsyncCustomerJourneyCanvas = () => {
  return (
    <Suspense fallback={null}>
      <PageControl code='customer_journey'>
        <CustomerJourney />
      </PageControl>
    </Suspense>
  )
}

const AsyncProfile = () => {
  return (
    <Suspense fallback={null}>
      <PageControl code='campaign_profile'>
        <Profile />
      </PageControl>
    </Suspense>
  )
}

const AsyncSwitchForCustomerJourney = ({ setStepData }) => {
  return (
    <Suspense fallback={null}>
      <SwitchForCustomerJourney setStepData={setStepData} />
    </Suspense>
  )
}

const AsyncSwitchForEvent = ({ setStepData }) => {
  return (
    <Suspense fallback={null}>
      <SwitchForEvent setStepData={setStepData} />
    </Suspense>
  )
}

const AsyncSwitchForUserTemplate = ({}) => {
  return (
    <Suspense fallback={null}>
      <SwitchForUserTemplate />
    </Suspense>
  )
}

const AsyncSwitchLayout = ({ stepData }) => {
  return (
    <Suspense fallback={null}>
      <SwitchLayout stepData={stepData} />
    </Suspense>
  )
}

const AsyncSwitchV2Layout = () => {
  return (
    <Suspense fallback={null}>
      <SwitchLayoutV2 />
    </Suspense>
  )
};

const AsyncSwitchEmailV2 = ({type}) => {
  return (
    <Suspense fallback={null}>
      <SwitchEmailV2 {...{ type }} />
    </Suspense>
  )
}

const AsyncSwitchTemplateV2 = ({ }) => {
  return (
    <Suspense fallback={null}>
      <SwitchTemplateV2 />
    </Suspense>
  )
}

const AsyncSwitchComponentV2 = ({ }) => {
  return (
    <Suspense fallback={null}>
      <SwitchComponentV2 />
    </Suspense>
  )
}

const AsyncSwitchColorSetV2 = () => (
  <Suspense fallback={null}>
    <SwitchColorSetV2 />
  </Suspense>
);

const AsyncSwitchBrandV2 = () => (
  <Suspense fallback={null}>
    <SwitchBrandV2 />
  </Suspense>
);

const AsyncSwitchBrandCreateV2 = () => (
  <Suspense fallback={null}>
    <SwitchBrandCreateV2 />
  </Suspense>
);

const AsyncSwitchColorSetCreateV2 = () => (
  <Suspense fallback={null}>
    <SwitchColorSetCreateV2 />
  </Suspense>
);

function CustomerJourneyToCampaignPlanRedirect() {
  const { id, nodeId } = useParams();

  if (nodeId) {
    return <Navigate to={generatePath("/campaign-plan/:id/:nodeId", { id, nodeId })} replace />;
  }

  return <Navigate to={generatePath("/campaign-plan/:id", { id })} replace />;
}
const AsyncCampaignsTimeline = () => {
  return (
    <Suspense fallback={null}>
      <PageControl code='campaigns_timeline'>
        <CampaignsTimeline />
      </PageControl>
    </Suspense>
  )
}

const AsyncAnnualCampaignMapsList = () => {
  return (
    <Suspense fallback={null}>
      <PageControl code='annual_campaign_maps'>
        <AnnualCampaignMapsList />
      </PageControl>
    </Suspense>
  )
}

const AsyncUserProfile = () => {
  return (
    <Suspense fallback={null}>
      <UserProfile />
    </Suspense>
  )
}

const AsyncAnnualCampaignMap = () => {
  return (
    <Suspense fallback={null}>
      <PageControl code='annual_campaign_maps'>
        <AnnualCampaignMap />
      </PageControl>
    </Suspense>
  )
}

export default function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const background = location.state && location.state.background;
  const [stepData, setStepData] = useState({ hasChanges: false, stepType: '' });
  const [refreshChecked, setRefreshChecked] = useState(false);
  const currentUser = useSelector(({ authentication }) => authentication.profile);
  const profileFetched = useSelector(({ authentication }) => authentication.profileFetched);
  const featureState = useSelector(featureSelector);

  useEffect(() => {
    // validate token is  about to expired
    let persist = JSON.parse(localStorage.getItem('persist:orchestra'));

    if (typeof persist !== 'undefined') {
      let token = JSON.parse(persist.authentication).accessToken;

      //code to bipass local static token
      if (
        token !== 'Bearer local_access_token' &&
        typeof token !== 'undefined' &&
        NODE_ENV !== 'development'
      ) {
        const { exp } = jwt_decode(token);

        // Refresh the token a minute early to avoid latency issues
        const expirationTime = exp * 1000 - 60000;

        if (Date.now() >= expirationTime) {
          localStorage.clear();
          dispatch({ type: RESET_TOKEN_SUCCESS });
          window.location.reload();
        } else {
          const authService = new AuthService(dispatch);
          addTokenToRequest(authService);
          createAuthRefreshInterceptor(axios, async failedRequest => {
            const tokens = await authService.refreshToken();
            dispatch({ type: REFRESH_TOKEN_SUCCESS, payload: tokens });

            failedRequest.response.config.headers['Authorization'] = 'Bearer ' + tokens.accessToken;

            return Promise.resolve();
          });
        }
      } else {
        const authService = new AuthService(dispatch);
        addTokenToRequest(authService);
        createAuthRefreshInterceptor(axios, async failedRequest => {
          const tokens = await authService.refreshToken();
          dispatch({ type: REFRESH_TOKEN_SUCCESS, payload: tokens });

          failedRequest.response.config.headers['Authorization'] = 'Bearer ' + tokens.accessToken;

          return Promise.resolve();
        });
      }
    }

    setRefreshChecked(true);

    dispatch(fetchProfile());
    dispatch(getStatusOptions());
    dispatch(fetchImpersonation());
  }, [dispatch]);

  useEffect(() => {
    if (!refreshChecked) return;
    // Wait for fetchProfile() to resolve this page load before deriving features
    // from currentUser. Otherwise, right after an impersonation stop, currentUser
    // is still the rehydrated impersonated profile and we'd fetch features for the
    // wrong profile id (rejected by ProfileRoleGuard on the backend).
    if (!profileFetched) return;

    if (!featureState.profile.fullfilled) {
      dispatch(fetchProfileFeatures(currentUser.profile?.id));
    }

    if (!featureState.country.fullfilled) {
      dispatch(fetchFeaturesForCountries(getUniqueUserCountries(currentUser).map(country => country.id)));
    }

    if (!featureState.team.fullfilled) {
      dispatch(fetchFeaturesForTeams(currentUser.teams ? currentUser.teams.map(team => team.id) : []));
    }
  }, [
    dispatch,
    featureState.profile.fullfilled,
    featureState.country.fullfilled,
    featureState.team.fullfilled,
    refreshChecked,
    profileFetched
  ]);

  const defaultPath = useMemo(() => {
    return decideHomePage(currentUser, featureState.country.data, featureState.profile.data, featureState.team.data);
  }, [currentUser, featureState.country.data, featureState.profile.data, featureState.team.data]);

  if (!(featureState.profile.fullfilled && featureState.country.fullfilled && featureState.team.fullfilled)) {
    return (
      <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }} >
        <Grid>
          <Grid container direction="column" alignItems="center">
            <Grid>
              <CircularProgress />
            </Grid>
            <Grid>
              <p>Authenticating</p>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={muiTheme}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={enLocale}
          dateFormats={{ keyboardDate: 'dd-MM-yyyy' }}
        >
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            autoHideDuration={2000}
            sx={{
              backgroundColor: '00a0df'
            }}
            Components={{
              imr_reminder_mail: ImrReminder,
              imr_reminder_fragment: ImrReminder
            }}
          >
            <CssBaseline />
            <Notifier />
            <Parent>
              <title>Orchestra</title>

              <Routes location={background || location}>
                {/* This is required because Switch use a custom Layout (Switch-specific TopBar) */}
                <Route path="/switch/v2" element={<AsyncSwitchV2Layout/>}>
                  <Route exact path="email/:displayId" element={<AsyncSwitchEmailV2 type="email" />} />
                  <Route exact path="event/:displayId" element={<AsyncSwitchEmailV2 type="event" />} />
                  <Route exact path="template/:templateVersionId" element={<AsyncSwitchTemplateV2 />} />
                  <Route exact path="component/:componentVersionId" element={<AsyncSwitchComponentV2 />} />
                  <Route exact path="colorset/new/:countryId" element={<AsyncSwitchColorSetCreateV2 />} />
                  <Route exact path="colorset/:colorSetVersionId" element={<AsyncSwitchColorSetV2 />} />
                  <Route exact path="brand/new/:countryId" element={<AsyncSwitchBrandCreateV2 />} />
                  <Route exact path="brand/:brandVersionId" element={<AsyncSwitchBrandV2 />} />
                </Route>
                {/* This is required because Switch use a custom Layout (Switch-specific TopBar) */}
                <Route path="/switch" element={<AsyncSwitchLayout stepData={stepData} />}>
                  { /* when user comes from Customer Journey with a stepDisplayID in URL */ }
                  <Route exact path="email/:stepDisplayId" element={<AsyncSwitchForCustomerJourney setStepData={setStepData} />} />
                  { /* when user comes from Home Page "Event fragment" flow */ }
                  <Route exact path="event/:campaignDisplayId/:stepDisplayId/:eventNumber/:templateId" element={<AsyncSwitchForEvent setStepData={setStepData} />} />
                  <Route exact path="fragment/:fragmentDisplayId" element={<AsyncSwitchForEvent setStepData={setStepData} />} />
                  { /* when user edit a user template */ }
                  <Route exact path="template/:userTemplateId" element={<AsyncSwitchForUserTemplate />} />
              </Route>
              <Route element={<MainLayout />}>
                <Route exact path="/campaign-plan/:id" element={<AsyncCustomerJourneyCanvas />} />
                <Route exact path="/campaign-plan/:id/:nodeId" element={<AsyncCustomerJourneyCanvas />} />
                <Route path="/customer-journey/:id" element={<CustomerJourneyToCampaignPlanRedirect />}/>
                <Route path="/customer-journey/:id/:nodeId" element={<CustomerJourneyToCampaignPlanRedirect />}/>

                <Route exact path="/campaign/profile/:campaign_id" element={<AsyncProfile />} />
                <Route exact path="/campaign/profile/:campaign_id/:node_id" element={<AsyncProfile />} />
                <Route path="/campaign" element={<AsyncCampaignOverview />}>
                  <Route index element={<AsyncCampaignList myList />} />
                  <Route path="list" element={<AsyncCampaignList />} />
                  <Route path="my-list" element={<AsyncCampaignList myList />} />
                  {/* adding this route, as we have to show the mylist page */}
                  {/* below and the campagin creation page above when on /campaign/creation route */}
                  {/* this works without the below route, but if fails when directly visiting /campaign/route . so adding it */}
                  <Route path="create" element={<AsyncCampaignList myList />} />
                  <Route path="" element={<Navigate to={`/campaign/my-list`} replace={true} />} />
                  <Route element={<AsyncPageNotFound />} />
                </Route>
                <Route path="/campaign-builder" element={<AsyncLightView />} />
                <Route path="/content-production" element={<AsyncContentProduction />} />
                <Route path="/content-production/:step_id" element={<AsyncContentProduction />} />
                <Route path="/timeline" element={<AsyncCampaignsTimeline />} />
                <Route path="/annual-campaign-maps" element={<AsyncAnnualCampaignMapsList />} />
                <Route path="/annual-campaign-maps/:displayId" element={<AsyncAnnualCampaignMap />} />
                <Route path="/user-profile" element={<AsyncUserProfile />} />
                <Route path="/access-denied" element={<AccessDenied />} />
                {/* Adding the auth_callback path to navigate to the appropriate page after authentication */}
                <Route
                  path="/auth_callback"
                  element={<Navigate to={defaultPath} replace />}
                />
                <Route path="/" element={<Navigate to={defaultPath} replace />} />
                <Route path="" element={<Navigate to={defaultPath} replace />} />
              </Route>
              <Route element={<PageNotFound />} />
            </Routes>
            {(background || location.pathname.includes('create')) && (
              <Routes>
                <Route path={`/campaign/create`} element={<AsyncCreateCampaign />} />
              </Routes>
            )}
            </Parent>
          </SnackbarProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
